import type { Libro } from "../api/tipos.ts";
import { esDeCerteza } from "../api/tipos.ts";

/**
 * El cuadre del mes, como el solape de dos conjuntos.
 *
 * Sustituye a cuatro tarjetas de estadística sueltas por una sola pregunta:
 * **¿cuánto de lo que entró y de lo que esperabas es la misma plata?**
 *
 *     ├──────── entró al banco ────────┤
 *              ├──── cuadrado ────┤
 *                       ├──────── esperabas cobrar ────────┤
 *     └ sin dueño ┘                    └ sin llegar ┘
 *
 * Vive aparte de React porque una barra que miente sobre proporciones es peor
 * que no tener barra: aquí se puede probar con números.
 */
export interface Cuadre {
  /** Todo lo que el banco reconoce, cuadrado o no. */
  entroCentavos: number;
  /** Lo que entró Y tiene dueño: el solape. */
  cuadradoCentavos: number;
  /** Entró y nadie sabe de quién es. */
  sinDuenoCentavos: number;
  /** Lo declarado que el banco no respalda: facturaste y no ha llegado. */
  sinLlegarCentavos: number;
  /** Lo que se esperaba cobrar en total. */
  esperadoCentavos: number;
  /** Anchos en porcentaje sobre la unión de los dos lados. Suman 100. */
  anchos: { sinDueno: number; cuadrado: number; sinLlegar: number };
}

export function calcularCuadre(libro: Libro): Cuadre {
  const movimientos = libro.movimientos ?? [];

  const entro = movimientos.reduce((s, m) => s + (m.montoCentavos ?? 0), 0);
  const cuadrado = movimientos
    .filter((m) => esDeCerteza(m.estado))
    .reduce((s, m) => s + (m.montoCentavos ?? 0), 0);

  // Los huérfanos son abonos declarados que el banco no respalda: es
  // exactamente "lo que esperabas y no llegó".
  const sinLlegar = (libro.alertas ?? []).reduce((s, h) => s + (h.montoCentavos ?? 0), 0);

  const sinDueno = Math.max(0, entro - cuadrado);
  const union = sinDueno + cuadrado + sinLlegar;

  // Sin un solo peso en ninguna parte, tres ceros: una barra vacía se dibuja
  // vacía en vez de repartir un 33% inventado a cada trozo.
  const pct = (v: number) => (union > 0 ? (v / union) * 100 : 0);

  return {
    entroCentavos: entro,
    cuadradoCentavos: cuadrado,
    sinDuenoCentavos: sinDueno,
    sinLlegarCentavos: sinLlegar,
    esperadoCentavos: cuadrado + sinLlegar,
    anchos: { sinDueno: pct(sinDueno), cuadrado: pct(cuadrado), sinLlegar: pct(sinLlegar) },
  };
}
